'use client'

import React, { useState } from 'react'
import { useParams, useSelectedLayoutSegment } from 'next/navigation'
import { useLocale, useTranslations } from 'next-intl'
import { clsx } from 'clsx'
import { Avatar, DropdownMenu, DropdownMenuItem, Icon } from '@node-space/ui-components'
import useTrackEvent from '@/app/hooks/useTrackEvent'
import { usePathname, useRouter } from '@/navigation'
import { Locale } from '@/types/locale'

interface Options {
  label: string
  value: string
}

const languageOptions: Options[] = [
  { label: 'translateEnglish', value: Locale.English },
  { label: 'translateChinese', value: Locale.SimplifiedChinese },
  { label: 'translateTurkish', value: Locale.Turkish },
  { label: 'translateFrench', value: Locale.French },
  // { label: 'translateArabic', value: Locale.Arabic },
  { label: 'translateSpanish', value: Locale.Spanish },
  { label: 'translatePortuguese', value: Locale.Portuguese },
  { label: 'translateJapanese', value: Locale.Japanese },
  { label: 'translateKorean', value: Locale.SouthKorean },
]

const SelectTranslation = () => {
  const segment = useSelectedLayoutSegment()
  const params = useParams()
  const router = useRouter()
  const pathname = usePathname()
  const locale = useLocale()
  const track = useTrackEvent()
  const t = useTranslations()
  const [isChangingLocale, setIsChangingLocale] = useState(false)

  const handleChange = (selectedOption: Options) => {
    if (selectedOption.value === locale) return

    setIsChangingLocale(true)

    track('Locale Changed', {
      category: 'Hosted Payments Page',
      payment_id: params?.uuid as string,
      flow: segment ?? '',
      version: 2.0,
      from_locale: locale,
      to_locale: selectedOption?.value,
    })

    router.replace(
      { pathname, params: params as never },
      { locale: selectedOption?.value as Locale }
    )
  }

  const dropdownMenuItems = languageOptions.map(option => (
    <DropdownMenuItem key={option.value} onClick={() => handleChange(option)} className="h-12">
      <div className="flex items-center gap-2">
        <Avatar type="language" name={option.value?.toLowerCase()} size="small" />
        {t(option.label)}
      </div>
    </DropdownMenuItem>
  ))

  const localeAvatarName = locale === 'en' ? 'globe' : locale

  return (
    <div className="absolute right-4 top-2 sm:right-6 sm:top-4">
      <DropdownMenu DropdownMenuItems={dropdownMenuItems} alignContent="end">
        <button
          className={clsx(
            'bg-surface-interactive flex items-center gap-2 rounded px-2 py-1.5 focus:outline-none focus:ring-2',
            { 'animate-pulse bg-transparent': isChangingLocale }
          )}
        >
          <Avatar type="language" name={localeAvatarName} size="tiny" />
          <span className="text-foreground-prominent text-sm font-normal">{`${locale?.toUpperCase()}`}</span>
          <Icon name="CaretDown" />
        </button>
      </DropdownMenu>
    </div>
  )
}

export default SelectTranslation
